import React, {useContext, useEffect, useState, useMemo, useCallback} from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { View, StyleSheet, Platform } from "react-native";
import Icon from 'react-native-vector-icons/FontAwesome';
import IonIcon from 'react-native-vector-icons/Ionicons';
import OrdersScreen from "./OrdersScreen";
import OnlineMapScreen from "./OnlineMapScreen";
import OnlineNewOrder from "./OnlineNewOrder";
import StatisticsHelper from "../helpers/StatisticsHelper";
import { OrdersContext } from "../helpers/OrdersContext";
import CircularButton from '../components/CircularButton';
import FunctionsScreen from "./FunctionsScreen";
import { useTranslation } from 'react-i18next';
import '../../i18n';
import {GestureHandlerRootView} from "react-native-gesture-handler";
import AsyncStorage from "@react-native-async-storage/async-storage";

const Tab = createBottomTabNavigator();

export default function OnlineScreen({ navigation }) {
    const { t } = useTranslation();
    const { orders } = useContext(OrdersContext);
    const { productsSum, statistics } = StatisticsHelper(orders);
    const [restaurantPlace, setRestaurantPlace] = useState({ _id: '' });

    useEffect(() => {
        const fetchRestaurantPlace = async () => {
            const restaurantPlaceStored = await AsyncStorage.getItem("restaurantPlace");
            if (restaurantPlaceStored) {
                setRestaurantPlace(JSON.parse(restaurantPlaceStored));
            }
        };
        fetchRestaurantPlace();
    }, []);

    // Memoize the screen options to prevent unnecessary re-renders
    const screenOptions = useMemo(() => ({
        headerShown: false,
        // Use newer screenOptions API instead of deprecated tabBarOptions
        tabBarActiveTintColor: '#a26909',
        tabBarInactiveTintColor: '#575757',
        tabBarActiveBackgroundColor: '#f0f0f0',
        tabBarInactiveBackgroundColor: '#ffffff',
        tabBarShowLabel: true,
        tabBarLabelStyle: {
            fontSize: 14,
            fontWeight: 'bold',
        },
        tabBarStyle: {
            height: 70,
            backgroundColor: '#e0e0e0',
            paddingBottom: Platform.OS === 'ios' ? 20 : 0,
        },
        tabBarItemStyle: {
            paddingVertical: 10,
            height: 70,
        },
        // Improve animation performance
        animationEnabled: false,
    }), []);

    // Memoize the badge count calculation
    const ordersBadgeCount = useMemo(() => {
        return (statistics?.onlineOrders?.filter(order => order.restaurantPlace === restaurantPlace?._id)?.length || 0) + 
               (statistics?.unClosedTakeAwayOrders?.filter(order => order.restaurantPlace === restaurantPlace?._id)?.length || 0);
    }, [statistics?.onlineOrders, statistics?.unClosedTakeAwayOrders, restaurantPlace?._id]);

    // Memoize the badge style
    const ordersBadgeStyle = useMemo(() => {
        return ordersBadgeCount === 0 
            ? { display: 'none' } 
            : { minWidth: "fit-content", height: "fit-content", borderRadius: 14, justifyContent: 'center', alignItems: 'center', overflow: "show" };
    }, [ordersBadgeCount]);

    // Memoize tab icons to prevent recreation on each render
    const mapIcon = useCallback(({ color, size }) => <Icon name="map-o" color={color} size={size} />, []);
    const bagIcon = useCallback(({ color, size }) => <IonIcon name="bag-sharp" color={color} size={size} />, []);
    const addIcon = useCallback(({ color, size }) => <IonIcon name="add-sharp" color={color} size={size} />, []);
    const serverIcon = useCallback(({ color, size }) => <Icon name="server" color={color} size={size} />, []);

    return (
        <View style={{ flex: 1, width: '100%', height: '100%', flexGrow: 1 }}>
            <Tab.Navigator
                detachInactiveScreens={true} // Changed to true for better performance
                tabBarLabelPosition={"below-icon"}
                lazy={true} // Changed to true for better initial load performance
                backBehavior={"history"}
                screenOptions={screenOptions}
            >
                <Tab.Screen name="MapView" component={OnlineMapScreen}
                            options={{
                                tabBarIcon: mapIcon,
                                title: t('map_view'),
                                tabBarLabel: t('map_view'),
                            }} />
                <Tab.Screen name="OrdersList" component={OrdersScreen}
                            options={{
                                tabBarIcon: bagIcon,
                                title: t('open_orders'),
                                tabBarLabel: t('open_orders'),
                                tabBarBadge: ordersBadgeCount,
                                tabBarBadgeStyle: ordersBadgeStyle
                            }}
                            initialParams={{ name: "OrdersList" }}
                />
                <Tab.Screen name="NewOrder" component={OnlineNewOrder}
                            options={{
                                tabBarIcon: addIcon,
                                title: t('new_order'),
                                tabBarLabel: t('new_order'),
                            }} />
                <Tab.Screen name="Functions" component={FunctionsScreen}
                            options={{
                                tabBarIcon: serverIcon,
                                title: t('functions_operations'),
                                tabBarLabel: t('functions_operations'),
                            }} />
            </Tab.Navigator>

            <CircularButton navigation={navigation} iconName="grid" />
        </View>
    );
}

const styles = StyleSheet.create({
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
